import type {
  CoachingGoal,
  CoachingGoalMilestone,
} from '@efdigital/ll-addon-data';
import { DataTypeCoaching } from '@efdigital/ll-addon-data/data-types';
import {
  createRecord,
  deleteRecord,
  query,
  updateRecord,
} from '@warp-drive/utilities/json-api';

import { jsonApiOptions, page } from '../../json-api.ts';

const MAX_MILESTONES_PER_PLAN = 100;
type MilestoneAttributes = Partial<
  Pick<CoachingGoalMilestone, 'title' | 'completed' | 'position'>
>;

export function queryCoachingGoalMilestonesByPlan(coachingPlanId: string) {
  return query<CoachingGoalMilestone>(
    DataTypeCoaching.CoachingGoalMilestone,
    {
      'filter[coachingPlan.id]': coachingPlanId,
      ...page({ offset: 0, limit: MAX_MILESTONES_PER_PLAN }),
      'sort[]': 'position',
    },
    jsonApiOptions
  );
}

export function queryCoachingGoalMilestonesByGoal(coachingGoalId: string) {
  return query<CoachingGoalMilestone>(
    DataTypeCoaching.CoachingGoalMilestone,
    {
      'filter[coachingGoal.id]': coachingGoalId,
      ...page({ offset: 0, limit: MAX_MILESTONES_PER_PLAN }),
      'sort[]': 'position',
    },
    jsonApiOptions
  );
}

export function createCoachingGoalMilestone(
  milestone: CoachingGoalMilestone,
  goal: CoachingGoal
) {
  const init = createRecord(milestone, jsonApiOptions);

  init.body = JSON.stringify(
    coachingGoalMilestoneCreateDocument(milestone, goal)
  );
  init.headers.set('Content-Type', 'application/vnd.api+json');
  return init;
}

export function coachingGoalMilestoneCreateDocument(
  milestone: Pick<CoachingGoalMilestone, 'title' | 'completed' | 'position'>,
  goal: Pick<CoachingGoal, 'id'>
) {
  return {
    data: {
      type: DataTypeCoaching.CoachingGoalMilestone,
      attributes: {
        title: milestone.title,
        completed: milestone.completed,
        position: milestone.position,
      },
      relationships: {
        coachingGoal: {
          data: {
            type: DataTypeCoaching.CoachingGoal,
            id: goal.id,
          },
        },
      },
    },
  };
}

export function updateCoachingGoalMilestone(
  milestone: CoachingGoalMilestone,
  attributes: MilestoneAttributes
) {
  const init = updateRecord(milestone, {
    ...jsonApiOptions,
    patch: true,
  });

  init.body = JSON.stringify(
    coachingGoalMilestoneUpdateDocument(milestone, attributes)
  );
  init.headers.set('Content-Type', 'application/vnd.api+json');
  return init;
}

export function coachingGoalMilestoneUpdateDocument(
  milestone: Pick<CoachingGoalMilestone, 'id'>,
  attributes: MilestoneAttributes
) {
  return {
    data: {
      type: DataTypeCoaching.CoachingGoalMilestone,
      id: milestone.id,
      attributes,
    },
  };
}

export function deleteCoachingGoalMilestone(milestone: CoachingGoalMilestone) {
  return deleteRecord(milestone, jsonApiOptions);
}
